/**
 * Native (Capacitor) entry-point handling.
 *
 * When the React app is loaded inside the Capacitor Android shell, we
 * want users to land on /m/home (the mobile menu grid built on Day 1)
 * regardless of whether the WebView started at "/" or some deep link.
 *
 * Detection: Capacitor exposes the global `Capacitor` object and a
 * `isNativePlatform()` helper. Falls back to the `capacitor://`
 * scheme check so this also works if the global isn't ready yet.
 *
 * This module also stamps `<html data-platform="native">` so any
 * platform-specific CSS / UI tweaks can hook off that attribute.
 */

export function isNativeApp() {
  // Capacitor sets `window.Capacitor` before our app code runs.
  if (typeof window === 'undefined') return false;
  if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
    try {
      return window.Capacitor.isNativePlatform();
    } catch {
      /* swallow and fall through to URL sniff */
    }
  }
  // Fallback: the bundled-mode WebView uses `https://localhost`
  // (Capacitor's androidScheme) which is unique to the native shell.
  return /^https:\/\/localhost(?::\d+)?/i.test(window.location.origin)
      && (window.navigator.userAgent || '').toLowerCase().includes('android');
}

export function redirectToMobileHomeIfNative() {
  if (!isNativeApp()) return;
  document.documentElement.setAttribute('data-platform', 'native');
  const path = window.location.pathname || '/';
  // Only auto-redirect from the root, login, or dashboard — leave
  // deeper paths (e.g. a notification deep-link) alone.
  const ROOT_PATHS = ['/', '/login', '/dashboard'];
  if (ROOT_PATHS.includes(path) && !path.startsWith('/m/')) {
    // Use replace so the back-button doesn't bounce them out.
    window.location.replace('/m/home');
  }
}
